import { useEffect, useState } from 'preact/hooks';
import {
  getRetention,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  type RecordRow,
  type RetentionApi,
  type RetentionState,
} from '../lib/api';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { humanizeValue } from '../lib/fieldDisplay';
import { AA, LINK, otherEnd } from '../lib/auditionSpread';
import { isProduction, missingTakeSent, orderTapes, readerOf, runningTime } from '../lib/selfTape';
import { RetentionChooser } from '../components/RetentionChooser';
import './Registry.css';
import './spread.css';
import './selfTape.css';

// A6 — THE TAPE LOG. Replaces the thin Registry on the Materials tab.
//
// ===================================================================
// A GALLERY ANSWERS THE WRONG QUESTION
// ===================================================================
// A3-A4 render PROGRESS. A5 renders OBLIGATIONS. This renders CRAFT, and
// craft is neither — it is a practice with a record, where the useful
// question is not *what do we have* but *WHAT DID WE DO, AND DID IT WORK.*
//
// A grid of thumbnails answers the first question. It makes every tape equal
// and the facts about MAKING them invisible, which is exactly what the
// registry standing here did. So this is a LOG: chronological, sparse, and
// arranged around the take that was sent.
//
// NEWEST FIRST, and that is not a default — it is the seam rule applied. A
// tape HAPPENED, like an audition; a shoot day is COMING. One screen over,
// A5 sorts the other way for the same reason. If these two ever agree,
// one of them is wrong.
//
// ===================================================================
// THE ROW'S SHAPE FOLLOWS ITS FACTS, NOT ITS KIND
// ===================================================================
// "No take sent" belongs on a self-tape and is noise on a headshot. The
// obvious implementation is `kind === 'self_tape'` — a copy of the pack's
// vocabulary in the browser, which is the thing this codebase keeps paying to
// avoid. So the treatment is keyed on the FACTS instead: a row carrying takes
// is a production record (lib/selfTape.ts's own header explains why that is
// the better test). This file never names a kind.

interface Props {
  tenantId: string;
  onOpenTape: (id: string) => void;
  /** F3 — the parent went looking for the plan that keeps everything. Where
   * that lands is the app's business, not this screen's. */
  onUpgrade: () => void;
}

interface State {
  loaded: boolean;
  tapes: RecordRow[];
  profiles: RecordRow[];
  /** id -> display name, for both reader pointers. */
  subjects: Map<string, string>;
  people: Map<string, string>;
  /** media id -> its links, for the reader and what-it-was-for lines. */
  linksBy: Record<string, import('../lib/api').LinkRow[]>;
  roles: Map<string, string>;
}

const EMPTY: State = {
  loaded: false,
  tapes: [],
  profiles: [],
  subjects: new Map(),
  people: new Map(),
  linksBy: {},
  roles: new Map(),
};

export function HouseholdTapes({ tenantId, onOpenTape, onUpgrade }: Props) {
  const [state, setState] = useState<State>(EMPTY);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [nonce, setNonce] = useState(0);
  const recordsVersion = useRecordsVersion();
  // F3 — THE 90-DAY RITUAL, on the screen where the tapes are.
  //
  // ⚑ null MEANS WE HAVE NOT READ IT, NOT "nothing is approaching". A failed
  // read shows NO badge and NO banner rather than a guessed one: telling a
  // family a choice is due when we do not know is worse than saying nothing,
  // and a household on a plan that keeps everything must never see this at
  // all (the server answers `forever` with an empty list, so that case is
  // structural rather than a check written here).
  const [retention, setRetention] = useState<RetentionApi | null>(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [retentionVersion, setRetentionVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void getRetention(tenantId)
      .then((r) => {
        if (!cancelled) setRetention(r);
      })
      .catch(() => {
        // Absent, not empty. See the state's own comment.
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, retentionVersion]);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loaded: false }));
    async function load() {
      try {
        const [media, profiles, subjects, people, roles] = await Promise.all([
          listAllRecordsWithLinks(tenantId, AA.MEDIA, {
            linkTypes: [LINK.MEDIA_SUBJECT, LINK.MEDIA_READER_SUBJECT, LINK.MEDIA_READER, LINK.MEDIA_ROLE],
          }),
          listAllRecordsByType(tenantId, AA.PROFILE).catch(() => [] as RecordRow[]),
          listAllRecordsByType(tenantId, AA.SUBJECT).catch(() => [] as RecordRow[]),
          listAllRecordsByType(tenantId, AA.PERSON).catch(() => [] as RecordRow[]),
          listAllRecordsByType(tenantId, AA.ROLE).catch(() => [] as RecordRow[]),
        ]);
        if (cancelled) return;
        setState({
          loaded: true,
          tapes: activeOnly(media.records),
          profiles: activeOnly(profiles),
          subjects: new Map(activeOnly(subjects).map((r) => [r.id, r.display_name])),
          people: new Map(activeOnly(people).map((r) => [r.id, r.display_name])),
          roles: new Map(activeOnly(roles).map((r) => [r.id, r.display_name])),
          linksBy: media.linksByRecordId,
        });
        setFailure(null);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setState((prev) => ({ ...prev, loaded: true }));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion, nonce]);

  if (failure) {
    return (
      <div class="registry">
        <div class="registry__eyebrow">Materials</div>
        <div class="registry__say">{failure.headline}</div>
        <div class="registry__say-soft">{failure.detail}</div>
        {failure.retryable ? (
          <button type="button" class="sp__retry" onClick={() => setNonce((n) => n + 1)}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  // A4's defect 2 — nothing claims an absence before the read lands.
  if (!state.loaded) {
    return (
      <div class="registry">
        <div class="registry__eyebrow">Materials</div>
        <div class="registry__say">One moment…</div>
      </div>
    );
  }

  // fileId -> the state the SERVER derived. The screen never computes an age
  // or reads a lever: two derivations of "due" is how a badge and a mail
  // start disagreeing about the same file.
  const retentionByFile = new Map<string, RetentionState>(
    (retention?.files ?? []).map((f) => [f.fileId, f.state])
  );
  const retentionOf = (t: RecordRow): RetentionState | null => {
    const ref = t.fields.original_file_ref;
    if (typeof ref !== 'string' || !ref) return null;
    return retentionByFile.get(ref) ?? null;
  };
  const dueCount = retention?.counts.due ?? 0;
  const warnedCount = retention?.counts.warned ?? 0;

  const order = orderTapes(state.tapes);

  const row = (t: RecordRow) => {
    const links = state.linksBy[t.id] ?? [];
    const reader = readerOf(links, t.id, { subjects: state.subjects, people: state.people }, {
      subject: LINK.MEDIA_READER_SUBJECT,
      person: LINK.MEDIA_READER,
    });
    const roleId = otherEnd(links, t.id, LINK.MEDIA_ROLE);
    const roleName = roleId ? state.roles.get(roleId) : null;
    const sent = t.fields.take_sent;
    const shot = t.fields.takes_shot;
    const dur = runningTime(t.fields.duration);
    const rowState = retentionOf(t);

    return (
      <button key={t.id} type="button" class="st__row" onClick={() => onOpenTape(t.id)}>
        <span class="st__row-top">
          <span class="st__row-name">
            {/* A12 — the has-video mark. A row whose tape can actually be
                watched says so before it is opened; a metadata-only row
                (logged but never attached) simply carries no mark, which is
                the honest difference rather than a broken player later. */}
            {typeof t.fields.original_file_ref === 'string' && t.fields.original_file_ref ? (
              <span class="st__has-video" aria-label="has a recording" title="Has the recording">▸</span>
            ) : null}
            {typeof t.fields.label === 'string' && t.fields.label.trim()
              ? t.fields.label
              : typeof t.fields.kind === 'string'
                ? humanizeValue(t.fields.kind)
                : 'Media'}
          </span>
          {/* WHICH TAKE WENT — the fact the whole surface is arranged around.
              The pack calls it the most-forgotten thing in a self-tape
              household and the first thing a coach asks. */}
          {typeof sent === 'number' ? (
            <span class="st__sent">
              take {sent}
              {typeof shot === 'number' ? <span class="st__of"> of {shot}</span> : null}
            </span>
          ) : missingTakeSent(t) ? (
            // Said ONCE, in words, and never as an alarm. A gap in a record
            // is not an emergency — and because this is keyed on the facts, a
            // headshot never reaches this branch and the line never becomes
            // something the parent learns to skip.
            <span class="st__nosent">no take marked as sent</span>
          ) : null}
          {/* F3 — the retention mark. WORDS, muted, never a dot: a coloured
              dot beside a tape reads as "this tape is broken", and this tape
              is perfectly fine. It is here, it is untouched, and somebody
              gets to decide about it. */}
          {rowState ? (
            <span class={`rt__badge${rowState === 'due' ? ' rt__badge--due' : ''}`}>
              {rowState === 'due' ? `past ${retention?.retentionDays} days` : `nearing ${retention?.retentionDays} days`}
            </span>
          ) : null}
        </span>
        <span class="st__row-sub">
          {[
            typeof t.fields.kind === 'string' ? humanizeValue(t.fields.kind) : null,
            roleName,
            dur,
            reader ? `${reader.name} read` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </button>
    );
  };

  return (
    <div class="registry">
      <div class="registry__eyebrow">Materials</div>

      {/* ── F3, THE 90-DAY RITUAL ──
          ⚑ ONLY A PERSON DESTROYS A FILE, NEVER A CLOCK. Nothing below acts;
          it opens a decision. The banner appears only when something has
          actually reached the window, and the quieter line only when
          something is approaching — a household with nothing due sees
          neither, and a household on a plan that keeps everything can never
          reach either branch (the server answers `forever` with no files, so
          no tier name appears on this screen at all). */}
      {retention && dueCount > 0 ? (
        chooserOpen ? (
          <RetentionChooser
            tenantId={tenantId}
            retention={retention}
            onUpgrade={onUpgrade}
            onChanged={() => setRetentionVersion((v) => v + 1)}
            onClose={() => setChooserOpen(false)}
          />
        ) : (
          <div class="rt">
            <div class="rt__head">
              <span class="rt__count">
                {dueCount} {dueCount === 1 ? 'file has' : 'files have'} passed {retention.retentionDays} days
              </span>
            </div>
            <p class="rt__say">
              They are still here and nothing is removed for you. When you want to, decide whether to
              keep them or free the room for new auditions.
            </p>
            <div class="rt__acts">
              <button type="button" class="rt__go" onClick={() => setChooserOpen(true)}>
                Decide
              </button>
            </div>
          </div>
        )
      ) : retention && warnedCount > 0 ? (
        <p class="rt__coming">
          {warnedCount} {warnedCount === 1 ? 'file is' : 'files are'} nearing {retention.retentionDays} days.
          Nothing changes on its own — you will choose what happens to them.
        </p>
      ) : null}

      <section>
        <h2 class="registry__sec">Tapes and materials</h2>
        {state.tapes.length === 0 ? (
          <div class="registry__empty">Nothing taped or filed yet.</div>
        ) : (
          <div class="st__list">
            {order.dated.map(row)}
            {order.undated.length > 0 ? <div class="st__divider">no date recorded</div> : null}
            {order.undated.map(row)}
          </div>
        )}
      </section>

      {/* The Materials tab used to carry profiles beside media. Dropping them
          when this screen replaced the registry would have quietly lost a
          record type, so they keep a home — a plain list, because a casting
          profile IS a list entry and building it a surface would be design
          applied where none is needed. */}
      <section>
        <h2 class="registry__sec">Casting profiles</h2>
        {state.profiles.length === 0 ? (
          <div class="registry__empty">No casting profiles yet.</div>
        ) : (
          <div class="st__list">
            {state.profiles.map((p) => (
              <button
                key={p.id}
                type="button"
                class="st__row"
                onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.PROFILE, id: p.id })}
              >
                <span class="st__row-top">
                  <span class="st__row-name">{p.display_name}</span>
                </span>
                {typeof p.fields.notes === 'string' && p.fields.notes.trim() ? (
                  <span class="st__row-sub">{p.fields.notes}</span>
                ) : null}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/** Every tape that is a production record — exported for the walk and for any
 *  later surface that wants "the tapes we actually made" rather than "every
 *  file we hold". Kept here rather than in lib/selfTape.ts because it is a
 *  screen's question, not a derivation. */
export function productionsOnly(rows: RecordRow[]): RecordRow[] {
  return rows.filter(isProduction);
}
